import { z } from 'zod';

import { NONE_VALUE } from '@/constants/answers';
import i18n from '@/lib/i18n';
import { fastApi } from '@/services/api';

const PATH = 'v1/customer/form/futures-appropriateness';

export const CURRENT_VERSION = 'v5';

export const DONT_KNOW_VALUE = 'DONT_KNOW' as const;

export const FUTURES_APPROPRIATENESS_FUTURES_KNOWLEDGE_STATEMENTS_ANSWERS = [
  'LEVERAGED_HIGH_RISK',
  'NEVER_LOSE_MONEY',
  'EXPOSURE_WITHOUT_OWNERSHIP',
  'SUITABLE_FOR_ALL_INVESTORS',
  'MARGIN_CALLS_OR_CLOSE_OUT',
  DONT_KNOW_VALUE,
] as const;

export const FUTURES_APPROPRIATENESS_MARGIN_REQUIREMENT_AT_LEVERAGE_ANSWERS = ['50', '100', '500', '1000', DONT_KNOW_VALUE] as const;

export const FUTURES_APPROPRIATENESS_MAINTENANCE_MARGIN_BREACH_ANSWERS = ['NOTHING', 'LIQUIDATION', 'FUNDS_CREDITED', 'POSITION_INCREASED', DONT_KNOW_VALUE] as const;

export const FUTURES_APPROPRIATENESS_MARGIN_WIPEOUT_PRICE_MOVE_ANSWERS = ['1', '5', '20', '50', DONT_KNOW_VALUE] as const;

export const FUTURES_APPROPRIATENESS_COMPLEX_CLASSIFICATION_REASON_ANSWERS = ['LEVERAGE_VOLATILITY_LOSS', 'GUARANTEED_RETURNS', 'SIMPLE_SAVINGS', DONT_KNOW_VALUE] as const;

export const FUTURES_APPROPRIATENESS_COMPLEX_TRANSACTION_COUNT_ANSWERS = [NONE_VALUE, 'UP_TO_9', 'FROM_10_TO_99', 'MORE_THAN_100'] as const;

export const AVERAGE_COMPLEX_TRANSACTION_VALUE_NOT_TRADED = 'NONE_OR_UNDER_100' as const;

export const FUTURES_APPROPRIATENESS_AVERAGE_COMPLEX_TRANSACTION_VALUE_ANSWERS = [
  AVERAGE_COMPLEX_TRANSACTION_VALUE_NOT_TRADED,
  'FROM_100_TO_999',
  'FROM_1000_TO_4999',
  'MORE_THAN_5000',
] as const;

export const FUTURES_APPROPRIATENESS_INVESTING_EXPERIENCE_LEVEL_ANSWERS = ['EXTENSIVE', 'SOME', 'LITTLE_OR_NONE'] as const;

export const FUTURES_APPROPRIATENESS_RISK_ATTITUDE_ANSWERS = ['AVOID_LOSSES', 'LIMITED_RISK', 'HIGH_RISK'] as const;

export const FUTURES_APPROPRIATENESS_OVERNIGHT_LOSS_REACTION_ANSWERS = ['FOLLOW_PLAN', 'PANIC', 'ACCEPT_VOLATILITY'] as const;

export const FUTURES_APPROPRIATENESS_TOTAL_LOSS_IMPACT_ANSWERS = ['SEVERE_HARDSHIP', 'MANAGEABLE', 'NO_IMPACT'] as const;

export const FUTURES_APPROPRIATENESS_LEVERAGE_ATTITUDE_ANSWERS = ['COMFORTABLE', 'CAUTIOUS', 'LIMITED_UNDERSTANDING'] as const;

export const futuresAppropriatenessAnswersSchema = z.object({
  futuresKnowledgeStatements: z.union(
    [
      z
        .array(
          z.enum(
            FUTURES_APPROPRIATENESS_FUTURES_KNOWLEDGE_STATEMENTS_ANSWERS.filter((answer) => answer !== DONT_KNOW_VALUE) as [
              Exclude<(typeof FUTURES_APPROPRIATENESS_FUTURES_KNOWLEDGE_STATEMENTS_ANSWERS)[number], typeof DONT_KNOW_VALUE>,
              ...Exclude<(typeof FUTURES_APPROPRIATENESS_FUTURES_KNOWLEDGE_STATEMENTS_ANSWERS)[number], typeof DONT_KNOW_VALUE>[],
            ],
          ),
          {
            error: i18n.t('requiredField.error'),
          },
        )
        .min(1, i18n.t('requiredField.error')),
      z.tuple([z.literal(DONT_KNOW_VALUE)]),
    ],
    {
      error: i18n.t('requiredField.error'),
    },
  ),
  marginRequirementAtLeverage: z.enum(FUTURES_APPROPRIATENESS_MARGIN_REQUIREMENT_AT_LEVERAGE_ANSWERS, {
    error: i18n.t('requiredField.error'),
  }),
  maintenanceMarginBreach: z.enum(FUTURES_APPROPRIATENESS_MAINTENANCE_MARGIN_BREACH_ANSWERS, {
    error: i18n.t('requiredField.error'),
  }),
  marginWipeoutPriceMove: z.enum(FUTURES_APPROPRIATENESS_MARGIN_WIPEOUT_PRICE_MOVE_ANSWERS, {
    error: i18n.t('requiredField.error'),
  }),
  complexClassificationReason: z.enum(FUTURES_APPROPRIATENESS_COMPLEX_CLASSIFICATION_REASON_ANSWERS, {
    error: i18n.t('requiredField.error'),
  }),
  complexTransactionCount: z.enum(FUTURES_APPROPRIATENESS_COMPLEX_TRANSACTION_COUNT_ANSWERS, {
    error: i18n.t('requiredField.error'),
  }),
  averageComplexTransactionValue: z.enum(FUTURES_APPROPRIATENESS_AVERAGE_COMPLEX_TRANSACTION_VALUE_ANSWERS, {
    error: i18n.t('requiredField.error'),
  }),
  investingExperienceLevel: z.enum(FUTURES_APPROPRIATENESS_INVESTING_EXPERIENCE_LEVEL_ANSWERS, {
    error: i18n.t('requiredField.error'),
  }),
  riskAttitude: z.enum(FUTURES_APPROPRIATENESS_RISK_ATTITUDE_ANSWERS, {
    error: i18n.t('requiredField.error'),
  }),
  overnightLossReaction: z.enum(FUTURES_APPROPRIATENESS_OVERNIGHT_LOSS_REACTION_ANSWERS, {
    error: i18n.t('requiredField.error'),
  }),
  totalLossImpact: z.enum(FUTURES_APPROPRIATENESS_TOTAL_LOSS_IMPACT_ANSWERS, {
    error: i18n.t('requiredField.error'),
  }),
  leverageAttitude: z.enum(FUTURES_APPROPRIATENESS_LEVERAGE_ATTITUDE_ANSWERS, {
    error: i18n.t('requiredField.error'),
  }),
});

export type FuturesAppropriatenessAnswersCompleted = z.infer<typeof futuresAppropriatenessAnswersSchema>;

export enum FuturesAppropriatenessResultStatus {
  PASSED = 0,
  MANUAL_REVIEW = 1,
  FAILED = 2,
}

type FuturesAppropriatenessResultPassed = {
  status: FuturesAppropriatenessResultStatus.PASSED;
  manualReview?: true;
};

type FuturesAppropriatenessResultManualReview = {
  status: FuturesAppropriatenessResultStatus.MANUAL_REVIEW;
};

type FuturesAppropriatenessResultFailedRetakeEligibilityTrue = {
  canRetake: true;
};

type FuturesAppropriatenessResultFailedRetakeEligibilityFalse = {
  canRetake: false;
  canRetakeFromDate: string;
};

type FuturesAppropriatenessResultFailed = {
  status: FuturesAppropriatenessResultStatus.FAILED;
  manualReview?: true;
} & (FuturesAppropriatenessResultFailedRetakeEligibilityTrue | FuturesAppropriatenessResultFailedRetakeEligibilityFalse);

type FuturesAppropriatenessResult = FuturesAppropriatenessResultPassed | FuturesAppropriatenessResultManualReview | FuturesAppropriatenessResultFailed;

const futuresAppropriatenessBaseSchema = z.object({
  date: z.iso.datetime(),
  // Stored records can be from an earlier version than the one this client submits
  version: z.string().optional(),
});

const futuresAppropriatenessSchema = z.discriminatedUnion('confirmed', [
  futuresAppropriatenessBaseSchema.extend({
    confirmed: z.literal(false),
    answers: futuresAppropriatenessAnswersSchema.partial(),
  }),
  futuresAppropriatenessBaseSchema.extend({
    confirmed: z.literal(true),
    answers: futuresAppropriatenessAnswersSchema,
  }),
]);

export type FuturesAppropriateness = z.infer<typeof futuresAppropriatenessSchema>;

export type FuturesAppropriatenessResulted = Extract<FuturesAppropriateness, { confirmed: true }> & { result: FuturesAppropriatenessResult };

type FuturesAppropriatenessGetResponse = FuturesAppropriateness | FuturesAppropriatenessResulted;

type FuturesAppropriatenessPostResponse = FuturesAppropriateness | FuturesAppropriatenessResulted;

export type FuturesAppropriatenessPostRequestBody = FuturesAppropriateness;

export const getFuturesAppropriateness = async () => {
  return fastApi
    .get(PATH)
    .json<FuturesAppropriatenessGetResponse>()
    .catch((error) => {
      if (error.response.status === 404) {
        return null;
      }
      throw error;
    });
};

export const postFuturesAppropriateness = async (data: Omit<FuturesAppropriatenessPostRequestBody, 'version'>) => {
  return fastApi.post(PATH, { json: { ...data, version: CURRENT_VERSION } }).json<FuturesAppropriatenessPostResponse>();
};
